import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ReactComponent as NavigationIcCheck24px } from '../../../assets/icons/navigation/ic_check_24px.svg';
import { ReactComponent as ContentIcRemove24px } from '../../../assets/icons/content/ic_remove_24px.svg';
import FormFieldContext from '../form-field/form-field.context';

const defaultIcons = {
  checked: <NavigationIcCheck24px />,
  indeterminate: <ContentIcRemove24px />
};

/**
 * @typedef Props
 * @prop  {React.ReactNode} children
 * @prop {string} [value]
 * @prop  {string} [className]
 * @prop {boolean} [checked]
 * @prop {boolean} [disabled]
 * @prop {{checked: React.ReactNode, indeterminate: React.ReactNode}} [icons]
 * @prop {string} [id]
 * @prop {boolean} [indeterminate]
 * @prop {string} [name]
 * @prop {() => void} [onChange]
 * @prop {boolean} [required]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifCheckbox = ({
  checked = false,
  children,
  className = '',
  disabled = false,
  icons: userIcons = {},
  id = '',
  indeterminate = false,
  name = '',
  onChange = null,
  required = false,
  value = '',
  ...other
}) => {
  const formFieldContext = useContext(FormFieldContext);

  const classes = classNames({
    'motif-checkbox': true,
    'motif-checkbox-label-wrap': true,
    'motif-checkbox-checked': checked,
    'motif-checkbox-disabled': disabled,
    'motif-checkbox-invalid': formFieldContext.invalid,
    'motif-checkbox-indeterminate': indeterminate,
    [className]: true
  });

  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  let ariaChecked;

  if (checked) {
    ariaChecked = 'true';
  } else if (indeterminate) {
    ariaChecked = 'mixed';
  } else {
    ariaChecked = 'false';
  }

  return (
    <label className={classes} htmlFor={id} data-motif-checkbox>
      <input
        {...other}
        aria-checked={ariaChecked}
        className="motif-checkbox-input motif-visually-hidden"
        defaultChecked={checked}
        disabled={disabled}
        id={id}
        indeterminate={indeterminate ? 1 : 0}
        name={name}
        onChange={onChange}
        required={required}
        type="checkbox"
        value={value}
      />

      <div className="motif-checkbox-custom">
        <span className="motif-checkbox-checked-icon">{icons.checked}</span>
        <span className="motif-checkbox-indeterminate-icon">{icons.indeterminate}</span>
      </div>

      <span className="motif-checkbox-label" aria-disabled={disabled}>
        {children}
      </span>
    </label>
  );
};

MotifCheckbox.propTypes = {
  checked: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  icons: PropTypes.shape({
    checked: PropTypes.node,
    indeterminate: PropTypes.node
  }),
  id: PropTypes.string,
  indeterminate: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  value: PropTypes.string
};

export { MotifCheckbox };
